import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const PRIORITY_CONFIG = {
  HIGH: { label: 'High Priority', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'AlertTriangle', iconColor: 'text-red-500' },
  MEDIUM: { label: 'Medium Priority', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'AlertCircle', iconColor: 'text-amber-500' },
  LOW: { label: 'Low Priority', bg: 'bg-[rgba(59,76,76,0.07)]', text: 'text-blue-700', border: 'border-blue-200', icon: 'Info', iconColor: 'text-blue-500' },
};

function FileIcon({ type }) {
  const config = {
    pdf: { icon: 'FileText', color: 'text-red-600', bg: 'bg-red-50' },
    xlsx: { icon: 'FileSpreadsheet', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  };
  const c = config?.[type] || config?.pdf;
  return (
    <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${c?.bg}`}>
      <Icon name={c?.icon} size={18} className={c?.color} />
    </div>
  );
}

export default function AnnouncementView({ announcement }) {
  const [acknowledged, setAcknowledged] = useState(announcement?.acknowledged || false);
  const [showToast, setShowToast] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');

  if (!announcement) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Icon name="Megaphone" size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Select an announcement to view</p>
        </div>
      </div>
    );
  }

  const cfg = PRIORITY_CONFIG?.[announcement?.priority];
  const date = new Date(announcement.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const handleAcknowledge = () => {
    setAcknowledged(true);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div className="flex-1 flex flex-col bg-white relative">
      {/* Toast */}
      {showToast && (
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium shadow-lg">
          <Icon name="CheckCircle" size={16} />
          Announcement acknowledged
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Priority banner */}
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border mb-5 ${cfg?.bg} ${cfg?.border}`}>
          <Icon name={cfg?.icon} size={16} className={cfg?.iconColor} />
          <span className={`text-sm font-semibold ${cfg?.text}`}>{cfg?.label}</span>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-slate-900 mb-3">{announcement?.title}</h2>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-5 text-sm text-slate-500">
          <span className="flex items-center gap-1.5">
            <Icon name="User" size={13} />
            {announcement?.from}
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="Calendar" size={13} />
            {date}
          </span>
          {announcement?.replies > 0 && (
            <span className="flex items-center gap-1.5">
              <Icon name="MessageSquare" size={13} />
              {announcement?.replies} {announcement?.replies === 1 ? 'reply' : 'replies'}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="prose prose-sm max-w-none mb-5">
          <p className="text-sm text-slate-700 leading-relaxed">{announcement?.body}</p>
        </div>

        {/* Attachments */}
        {announcement?.attachments?.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Attachments</h4>
            <div className="space-y-2">
              {announcement?.attachments?.map((att, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50">
                  <FileIcon type={att?.type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{att?.name}</p>
                    <p className="text-xs text-slate-500">{att?.size}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors">
                      <Icon name="Eye" size={14} />
                    </button>
                    <button className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors">
                      <Icon name="Download" size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setReplyOpen(r => !r)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Icon name="Reply" size={14} />
            Reply Thread
          </button>
          {announcement?.priority === 'HIGH' && (
            <button
              onClick={handleAcknowledge}
              disabled={acknowledged}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                acknowledged
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default' :'bg-[#3b4c4c] text-white hover:bg-[#162d4a]'
              }`}
            >
              <Icon name={acknowledged ? 'CheckCircle' : 'Check'} size={14} />
              {acknowledged ? 'Acknowledged' : 'Acknowledge'}
            </button>
          )}
        </div>

        {/* Reply thread */}
        {replyOpen && (
          <div className="mt-5 border-l-2 border-[#3b4c4c]/30 pl-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Reply Thread</h4>
            <div className="space-y-3 mb-3">
              <p className="text-sm text-slate-400 italic">No replies yet. Be the first to respond.</p>
            </div>
            <div className="relative">
              <textarea
                value={replyText}
                onChange={e => setReplyText(e?.target?.value)}
                placeholder="Write a reply…"
                rows={2}
                className="w-full px-3 py-2 pr-16 text-sm bg-slate-50 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 focus:border-[#3b4c4c]/40 placeholder-slate-400"
              />
              <button
                onClick={() => setReplyText('')}
                disabled={!replyText?.trim()}
                className={`absolute right-2 bottom-2 flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
                  replyText?.trim() ? 'bg-[#3b4c4c] text-white hover:bg-[#162d4a]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <Icon name="Send" size={13} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
